export interface RuntimeErrorRecord {
  context: string;
  message: string;
  details: string;
  report: string;
}

type RuntimeErrorListener = (record: RuntimeErrorRecord) => void;

const listeners = new Set<RuntimeErrorListener>();
let lastErrorKey: string | null = null;
let lastRecord: RuntimeErrorRecord | null = null;

export function subscribeRuntimeErrors(listener: RuntimeErrorListener): () => void {
  listeners.add(listener);
  if (lastRecord) listener(lastRecord);
  return () => listeners.delete(listener);
}

export function reportRuntimeError(cause: unknown, context = "Runtime error"): RuntimeErrorRecord | null {
  const formatted = formatRuntimeError(cause);
  const key = `${context}:${formatted.message}:${formatted.details}`;
  // A failing frame loop can throw sixty times a second; only surface the first.
  if (key === lastErrorKey) return null;
  lastErrorKey = key;
  const diagnostics = runtimeDiagnostics();
  const record: RuntimeErrorRecord = {
    context,
    message: formatted.message,
    details: `${formatted.details}\n\nRuntime diagnostics:\n${diagnostics}`,
    report: `${context}: ${formatted.message}\n\n${formatted.details}\n\nRuntime diagnostics:\n${diagnostics}`,
  };
  lastRecord = record;
  for (const listener of listeners) listener(record);
  console.error(context, cause);
  return record;
}

export function resetRuntimeErrorsForTests(): void {
  lastErrorKey = null;
  lastRecord = null;
}

export function formatRuntimeError(cause: unknown): { message: string; details: string } {
  if (cause instanceof Error) {
    return { message: cause.message || "Unknown error", details: formatErrorChain(cause) };
  }
  if (typeof cause === "string") return { message: cause, details: cause };
  try {
    const serialized = JSON.stringify(cause, null, 2) ?? String(cause);
    return { message: serialized, details: serialized };
  } catch {
    return { message: String(cause), details: String(cause) };
  }
}

function formatErrorChain(error: Error): string {
  const lines: string[] = [];
  const seen = new Set<unknown>();
  const visit = (value: unknown, label: string): void => {
    if (seen.has(value)) return;
    seen.add(value);
    if (value instanceof Error) {
      lines.push(`${label}: ${value.stack ?? value.toString()}`);
      const componentStack = (value as Error & { componentStack?: unknown }).componentStack;
      if (typeof componentStack === "string" && componentStack.trim()) {
        lines.push(`${label} component stack:\n${componentStack}`);
      }
      const aggregate = (value as Error & { errors?: unknown }).errors;
      if (Array.isArray(aggregate)) aggregate.forEach((item, index) => visit(item, `${label} [${index + 1}]`));
      if ("cause" in value && value.cause !== undefined) visit(value.cause, `${label} cause`);
      return;
    }
    try {
      lines.push(`${label}: ${typeof value === "string" ? value : JSON.stringify(value) ?? String(value)}`);
    } catch {
      lines.push(`${label}: ${String(value)}`);
    }
  };
  visit(error, "Error");
  return lines.join("\n\n") || String(error);
}

function runtimeDiagnostics(): string {
  if (typeof window === "undefined" || typeof navigator === "undefined") return "Runtime diagnostics unavailable.";
  const canvas = document.querySelector<HTMLCanvasElement>("canvas");
  let webgl = "unavailable";
  let renderer = "unknown";
  let vendor = "unknown";
  try {
    const context = canvas?.getContext("webgl2") ?? canvas?.getContext("webgl");
    if (context) {
      webgl = context.constructor.name === "WebGL2RenderingContext" ? "webgl2" : "webgl";
      const debugInfo = context.getExtension("WEBGL_debug_renderer_info");
      if (debugInfo) {
        renderer = String(context.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL));
        vendor = String(context.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL));
      }
    }
  } catch {
    webgl = "error while probing";
  }
  const deviceMemory = "deviceMemory" in navigator
    ? String((navigator as Navigator & { deviceMemory?: number }).deviceMemory)
    : "unknown";
  return [
    `URL: ${window.location.href}`,
    `Viewport: ${window.innerWidth}x${window.innerHeight}`,
    `Device pixel ratio: ${window.devicePixelRatio}`,
    `Online: ${navigator.onLine}`,
    `Visibility: ${document.visibilityState}`,
    `Device memory: ${deviceMemory} GB`,
    `WebGL: ${webgl}`,
    `WebGL vendor: ${vendor}`,
    `WebGL renderer: ${renderer}`,
    `User agent: ${navigator.userAgent}`,
  ].join("\n");
}
